/**
 * DonorContacts.jsx — Phase 1
 * Manage key contacts within a donor organisation.
 * Shows contacts grouped by donor; supports filtering by donorId.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  getDonorContacts, createDonorContact, updateDonorContact, deleteDonorContact,
  getDonors, SENIORITY_LEVELS, COMM_TYPES, fmtDate,
} from '../../../api/donorsApi';

// ── Seniority badge ───────────────────────────────────────────────────────────
function SeniorityBadge({ value }) {
  const colors = {
    'decision-maker': { bg: '#EEF2FF', color: '#4338CA' },
    'influencer':     { bg: '#F0FDF4', color: '#16A34A' },
    'coordinator':    { bg: '#FFF7ED', color: '#C2410C' },
    'staff':          { bg: '#F9FAFB', color: '#6B7280' },
  };
  const c = colors[value] || colors.staff;
  const label = SENIORITY_LEVELS.find(s => s.value === value)?.label || value;
  return (
    <span className="bb-dn-badge" style={{ background: c.bg, color: c.color }}>
      {label}
    </span>
  );
}

// ── Contact Form Modal ────────────────────────────────────────────────────────
function ContactFormModal({ contact, orgId, donors, onClose, onSaved }) {
  const isEdit = !!contact;
  const [form, setForm] = useState({
    donorId:    contact?.donorId    || (donors[0]?.id || ''),
    name:       contact?.name       || '',
    email:      contact?.email      || '',
    phone:      contact?.phone      || '',
    role:       contact?.role       || '',
    seniority:  contact?.seniority  || 'staff',
    notes:      contact?.notes      || '',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim())    { setError('Contact name is required.'); return; }
    if (!form.donorId)        { setError('Please select a donor organisation.'); return; }
    setSaving(true); setError('');
    try {
      if (isEdit) await updateDonorContact(contact.id, form);
      else         await createDonorContact(orgId, form);
      onSaved();
    } catch (e) {
      setError(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bb-dn-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bb-dn-modal">
        <div className="bb-dn-modal-header">
          <h3>{isEdit ? 'Edit Contact' : 'Add New Contact'}</h3>
          <button className="bb-dn-modal-close" onClick={onClose}>✕</button>
        </div>

        {error && <div className="bb-dn-alert bb-dn-alert--error">{error}</div>}

        <div className="bb-dn-form-grid">
          <div className="bb-dn-form-group bb-dn-full">
            <label>Donor Organisation <span className="bb-dn-required">*</span></label>
            <select className="bb-dn-select" value={form.donorId} onChange={e => set('donorId', e.target.value)}>
              <option value="">— Select donor —</option>
              {donors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>

          <div className="bb-dn-form-group bb-dn-full">
            <label>Full Name <span className="bb-dn-required">*</span></label>
            <input className="bb-dn-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Jane Smith" />
          </div>

          <div className="bb-dn-form-group">
            <label>Email</label>
            <input className="bb-dn-input" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="jane@example.com" />
          </div>

          <div className="bb-dn-form-group">
            <label>Phone</label>
            <input className="bb-dn-input" type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+44..." />
          </div>

          <div className="bb-dn-form-group">
            <label>Role / Job Title</label>
            <input className="bb-dn-input" value={form.role} onChange={e => set('role', e.target.value)} placeholder="e.g. CSR Manager" />
          </div>

          <div className="bb-dn-form-group">
            <label>Seniority Level</label>
            <select className="bb-dn-select" value={form.seniority} onChange={e => set('seniority', e.target.value)}>
              {SENIORITY_LEVELS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          <div className="bb-dn-form-group bb-dn-full">
            <label>Notes</label>
            <textarea className="bb-dn-textarea" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any notes about this contact…" />
          </div>
        </div>

        <div className="bb-dn-modal-footer">
          <button className="bb-dn-btn bb-dn-btn-secondary" onClick={onClose}>Cancel</button>
          <button className="bb-dn-btn bb-dn-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Contact'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function DonorContacts({ orgId, filterDonorId = null }) {
  const [contacts, setContacts] = useState([]);
  const [donors,   setDonors]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [seniorityFilter, setSeniorityFilter] = useState('all');
  const [donorFilter,     setDonorFilter]     = useState(filterDonorId || 'all');
  const [showForm,  setShowForm]  = useState(false);
  const [editItem,  setEditItem]  = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting]  = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, d] = await Promise.all([
        getDonorContacts(orgId, filterDonorId),
        getDonors(orgId),
      ]);
      setContacts(c);
      setDonors(d);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [orgId, filterDonorId]);

  useEffect(() => { load(); }, [load]);

  const filtered = contacts.filter(c => {
    const matchSearch   = !search || c.name?.toLowerCase().includes(search.toLowerCase()) || c.role?.toLowerCase().includes(search.toLowerCase());
    const matchSeniority = seniorityFilter === 'all' || c.seniority === seniorityFilter;
    const matchDonor    = donorFilter === 'all' || c.donorId === donorFilter;
    return matchSearch && matchSeniority && matchDonor;
  });

  const getDonorName = id => donors.find(d => d.id === id)?.name || '—';

  const handleDelete = async () => {
    setDeleting(true);
    try { await deleteDonorContact(deleteItem.id); setDeleteItem(null); load(); }
    catch (e) { console.error(e); }
    finally { setDeleting(false); }
  };

  // Days since last contact
  const daysSince = (ts) => {
    if (!ts) return null;
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return Math.floor((Date.now() - d) / 86400000);
  };

  if (loading) return <div className="bb-dn-loading"><div className="bb-loading-spinner" /><p>Loading contacts…</p></div>;

  return (
    <div className="bb-dn-section">
      <div className="bb-dn-section-header">
        <div>
          <h2 className="bb-dn-section-title">Contact CRM</h2>
          <p className="bb-dn-section-subtitle">Track key people within donor organisations</p>
        </div>
        <button className="bb-dn-btn bb-dn-btn-primary" onClick={() => { setEditItem(null); setShowForm(true); }}>
          + Add Contact
        </button>
      </div>

      <div className="bb-dn-filters">
        <input
          className="bb-dn-input bb-dn-search"
          placeholder="Search by name or role…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {!filterDonorId && (
          <select className="bb-dn-select" value={donorFilter} onChange={e => setDonorFilter(e.target.value)}>
            <option value="all">All Donors</option>
            {donors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        )}
        <select className="bb-dn-select" value={seniorityFilter} onChange={e => setSeniorityFilter(e.target.value)}>
          <option value="all">All Seniority</option>
          {SENIORITY_LEVELS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <span className="bb-dn-count">{filtered.length} contact{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {filtered.length === 0 ? (
        <div className="bb-dn-empty">
          <div className="bb-dn-empty-icon">👤</div>
          <p>{contacts.length === 0 ? 'No contacts added yet. Click "Add Contact" to get started.' : 'No contacts match your filters.'}</p>
        </div>
      ) : (
        <div className="bb-dn-table-wrap">
          <table className="bb-dn-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Organisation</th>
                <th>Role</th>
                <th>Seniority</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Last Contact</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const since = daysSince(c.lastContactedAt);
                const lapsing = since !== null && since > 30;
                return (
                  <tr key={c.id} className="bb-dn-table-row">
                    <td><strong>{c.name}</strong></td>
                    <td className="bb-dn-muted">{getDonorName(c.donorId)}</td>
                    <td className="bb-dn-muted">{c.role || '—'}</td>
                    <td><SeniorityBadge value={c.seniority} /></td>
                    <td>
                      {c.email
                        ? <a href={`mailto:${c.email}`} className="bb-dn-link">{c.email}</a>
                        : <span className="bb-dn-muted">—</span>}
                    </td>
                    <td className="bb-dn-muted">{c.phone || '—'}</td>
                    <td>
                      {since === null
                        ? <span className="bb-dn-muted">Never</span>
                        : <span className={lapsing ? 'bb-dn-lapse-warn' : ''}>{since}d ago{lapsing ? ' ⚠' : ''}</span>}
                    </td>
                    <td>
                      <div className="bb-dn-row-actions">
                        <button className="bb-dn-action-btn" onClick={() => { setEditItem(c); setShowForm(true); }}>Edit</button>
                        <button className="bb-dn-action-btn bb-dn-action-btn--danger" onClick={() => setDeleteItem(c)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <ContactFormModal
          contact={editItem}
          orgId={orgId}
          donors={donors}
          onClose={() => { setShowForm(false); setEditItem(null); }}
          onSaved={() => { setShowForm(false); setEditItem(null); load(); }}
        />
      )}

      {deleteItem && (
        <div className="bb-dn-modal-overlay" onClick={e => e.target === e.currentTarget && setDeleteItem(null)}>
          <div className="bb-dn-modal" style={{ maxWidth: 440 }}>
            <div className="bb-dn-modal-header">
              <h3>Delete Contact</h3>
              <button className="bb-dn-modal-close" onClick={() => setDeleteItem(null)}>✕</button>
            </div>
            <p style={{ padding: '16px 0', color: 'var(--bb-gray)' }}>
              Delete <strong>{deleteItem.name}</strong>? This cannot be undone.
            </p>
            <div className="bb-dn-modal-footer">
              <button className="bb-dn-btn bb-dn-btn-secondary" onClick={() => setDeleteItem(null)}>Cancel</button>
              <button className="bb-dn-btn bb-dn-btn-danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete Contact'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
